import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal } from '../elements/Modal';
import { Icon } from '../elements/icon';
import Omega3ProductImg from '../../assets/omega3/1.webp';
import Omega3CapsuleHandImg from '../../assets/omega3/2.webp';
import Omega3LabelImg from '../../assets/omega3/4.webp';
import Omega3LabelDeImg from '../../assets/omega3/4de.webp';
import { getBrowserLocale } from '../../utils/language';

const Omega3OrderUpsell = ({
  originalPrice = 25,
  discountedPrice = 18,
  discountPercent = 30,
  durationLabel = '1 month',
  onAddToOrder,
  onDismiss,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  const normalizedOriginalPrice = Number(originalPrice) || 0;
  const normalizedDiscountedPrice = Number(discountedPrice) || 0;
  const normalizedDiscountPercent =
    Number(discountPercent) ||
    (normalizedOriginalPrice > 0
      ? Math.round(((normalizedOriginalPrice - normalizedDiscountedPrice) / normalizedOriginalPrice) * 100)
      : 0);

  const galleryImages = useMemo(
    () => [
      { src: Omega3ProductImg, altKey: 'Omega3_Media_Alt_Product' },
      { src: Omega3CapsuleHandImg, altKey: 'Omega3_Media_Alt_Softgels' },
      {
        src: i18n.language?.startsWith('de') ? Omega3LabelDeImg : Omega3LabelImg,
        altKey: 'Omega3_Media_Alt_Label',
      },
    ],
    [i18n.language],
  );

  const priceFmt = new Intl.NumberFormat(getBrowserLocale(i18n.language), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const activeImage = activeImageIndex === null ? null : galleryImages[activeImageIndex];

  const handleDismiss = () => {
    onDismiss?.();
  };

  const handleAddToOrder = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (typeof onAddToOrder === 'function') {
        await onAddToOrder({ duration: durationLabel });
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, 800));
      }
      onSuccess?.();
    } catch (e) {
      toast.error(e?.data?.message || e?.message || t('OrderUpsell_Error_Generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleNextImage = (direction) => {
    setActiveImageIndex((currentIndex) => {
      if (currentIndex === null) return currentIndex;
      return (currentIndex + direction + galleryImages.length) % galleryImages.length;
    });
  };

  return (
    <section aria-label={t('OrderUpsell_Aria')} className="t-flex t-w-full t-flex-col t-gap-3.5">
      <span className="t-w-fit t-rounded-full t-bg-butter t-px-3 t-py-1 t-text-[10.5px] t-font-bold t-text-navy">
        {t('OrderUpsell_Badge')}
      </span>

      <h2 className="!t-m-0 t-font-serif t-text-[23px] t-font-normal t-leading-10 t-text-navy">
        {t('OrderUpsell_Headline_Start')} <em className="t-text-teal-default">Omega-3</em>{' '}
        <mark className="t-rounded t-bg-butter t-px-1 t-text-navy">
          {t('OrderUpsell_Headline_Discount', { percent: normalizedDiscountPercent })}
        </mark>
      </h2>

      <div className="t-flex t-flex-col t-gap-2.5 t-rounded-xl t-border t-border-gray-200 t-bg-surface t-p-2.5">
        <div className="t-flex t-gap-2">
          {galleryImages.map((image, index) => (
            <button
              key={image.altKey}
              type="button"
              onClick={() => setActiveImageIndex(index)}
              className="t-relative t-flex t-h-[72px] t-w-[72px] t-flex-shrink-0 t-cursor-zoom-in t-items-center t-justify-center t-overflow-hidden t-rounded-[10px] t-border t-border-gray-200 t-bg-white t-p-0 hover:t-border-teal-default"
            >
              <img src={image.src} alt={t(image.altKey)} className="t-h-full t-w-full t-object-contain" />
              <span className="t-absolute t-bottom-[3px] t-right-[3px] t-flex t-h-4 t-w-4 t-items-center t-justify-center t-rounded-full t-bg-navy/75 t-text-white">
                <Icon raw name="orderUpsellZoom" className="t-h-[9px] t-w-[9px]" />
              </span>
            </button>
          ))}
        </div>
        <div>
          <p className="!t-m-0 t-text-[12.5px] t-font-bold t-leading-tight t-text-teal-default">
            {t('OrderUpsell_Product_Name')}
          </p>
          <p className="!t-m-0 t-text-[10.5px] t-leading-snug t-text-gray-500">
            {t('OrderUpsell_Product_Specs')}
          </p>
        </div>
      </div>

      <p className="!t-m-0 t-text-[12.5px] t-leading-normal t-text-gray-500">{t('OrderUpsell_Subhead')}</p>

      <div className="t-flex t-items-start t-gap-2 t-text-[11.5px] t-leading-normal t-text-gray-500">
        <Icon raw name="orderUpsellBrain" className="t-mt-0.5 t-h-[18px] t-w-[18px] t-flex-shrink-0 t-text-teal-default" />
        <span>
          <strong className="t-text-navy">{t('OrderUpsell_Benefit_Brain_Title')}</strong>
          {t('OrderUpsell_Benefit_Brain_Desc')}
        </span>
      </div>
      <div className="t-flex t-items-start t-gap-2 t-text-[11.5px] t-leading-normal t-text-gray-500">
        <Icon raw name="orderUpsellHeart" className="t-mt-0.5 t-h-[18px] t-w-[18px] t-flex-shrink-0 t-text-teal-default" />
        <span>
          <strong className="t-text-navy">{t('OrderUpsell_Benefit_Heart_Title')}</strong>
          {t('OrderUpsell_Benefit_Heart_Desc')}
        </span>
      </div>

      <div className="t-flex t-flex-col t-gap-1.5 t-rounded-xl t-border t-border-teal-mid t-bg-teal-bg t-p-3">
        <span className="t-text-[9.5px] t-font-extrabold t-uppercase t-tracking-[0.05em] t-text-teal-default">
          {t('OrderUpsell_Offer_Banner')}
        </span>
        <div className="t-flex t-items-baseline t-gap-2">
          <span className="t-text-[12.5px] t-text-gray-500 t-line-through">
            {priceFmt.format(normalizedOriginalPrice)}
          </span>
          <span className="t-text-[21px] t-font-extrabold t-text-navy">
            {priceFmt.format(normalizedDiscountedPrice)}
          </span>
        </div>
        <span className="t-text-[11px] t-leading-snug t-text-gray-500">
          {t('OrderUpsell_DealNote', { percent: normalizedDiscountPercent })}
        </span>
      </div>

      <div className="t-flex t-flex-col t-gap-2">
        <button
          type="button"
          disabled={loading}
          onClick={handleAddToOrder}
          className="t-w-full t-rounded-[11px] t-border-0 t-bg-teal-default t-px-4 t-py-3 t-text-[13.5px] t-font-bold t-text-white hover:t-bg-navy disabled:t-opacity-60"
        >
          {loading ? t('OrderUpsell_Loading') : t('OrderUpsell_Cta')}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={loading}
          className="t-w-full t-border-0 t-bg-transparent t-px-1.5 t-py-1 t-text-center t-text-[11.5px] t-text-gray-500 t-underline disabled:t-opacity-60"
        >
          {t('OrderUpsell_Dismiss')}
        </button>
      </div>

      <Modal
        open={activeImageIndex !== null}
        handleClose={() => setActiveImageIndex(null)}
        className="!t-max-w-[95vw] lg:t-max-w-[760px]"
      >
        <div className="t-relative t-p-2 md:t-p-4">
          {activeImage && (
            <>
              <button
                type="button"
                onClick={() => handleNextImage(-1)}
                className="t-absolute t-left-3 t-top-1/2 t-flex t-h-9 t-w-9 t--translate-y-1/2 t-items-center t-justify-center t-rounded-full t-border-0 t-bg-white/90 t-text-navy"
                aria-label={t('Omega3_Gallery_Previous')}
              >
                <Icon raw name="orderUpsellChevronLeft" className="t-h-5 t-w-5" />
              </button>
              <img
                src={activeImage.src}
                alt={t(activeImage.altKey)}
                className="t-h-auto t-max-h-[80vh] t-w-full t-rounded-xl t-bg-white t-object-contain"
              />
              <button
                type="button"
                onClick={() => handleNextImage(1)}
                className="t-absolute t-right-3 t-top-1/2 t-flex t-h-9 t-w-9 t--translate-y-1/2 t-items-center t-justify-center t-rounded-full t-border-0 t-bg-white/90 t-text-navy"
                aria-label={t('Omega3_Gallery_Next')}
              >
                <Icon raw name="orderUpsellChevronRight" className="t-h-5 t-w-5" />
              </button>
              <span className="t-absolute t-bottom-5 t-left-1/2 t--translate-x-1/2 t-rounded-full t-bg-white/90 t-px-3 t-py-1 t-text-xs t-font-bold t-text-navy">
                {activeImageIndex + 1} / {galleryImages.length}
              </span>
            </>
          )}
        </div>
      </Modal>
    </section>
  );
};

export default Omega3OrderUpsell;
